import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import {
  OPAQUE_TEXTURED_PANEL_STYLE, FourCorners,
  GOLD, TEXT_BODY, TEXT_PRIMARY, TEXT_MUTED,
} from '../lib/medievalTheme';
import { classCurrencies } from '../utils/arena/classCurrencies';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { VialGauge } from './play/ForesightGauge';
import AlchemistPlate from './laboratory/AlchemistPlate';
import { t } from '../localization/t';

// The header's class button: the class's major-node ornament as a
// round icon-only button, and under it a small plate listing the class's
// meta currencies (classCurrencies.js). Reachable from any screen, in a
// match or out of it. Renders nothing until a class is held.
export default class ClassCurrenciesButton extends Component {
  state = { open: false };

  componentWillUnmount() { this.close(); }

  toggle = () => (this.state.open ? this.close() : this.open());

  open = () => {
    this.setState({ open: true });
    // Same dismissal as the mixer popover: a document click outside the
    // plate (decided in capture, acted on in bubble, attached a task
    // later so the opening click never counts), and Escape via the stack.
    this._attachTimer = setTimeout(() => {
      document.addEventListener('click', this.onDocCapture, true);
      document.addEventListener('click', this.onDocClick);
    }, 0);
    this._esc = pushDismiss(this.close);
  };

  close = () => {
    clearTimeout(this._attachTimer);
    document.removeEventListener('click', this.onDocCapture, true);
    document.removeEventListener('click', this.onDocClick);
    if (this._esc != null) { removeDismiss(this._esc); this._esc = null; }
    if (this.state.open) this.setState({ open: false });
  };

  onDocCapture = (e) => { this._inside = this.root?.contains(e.target) === true; };
  onDocClick = () => { if (!this._inside) this.close(); };

  render() {
    const found = classCurrencies(this.props.profile);
    if (!found) return null;
    const { cls, currencies } = found;
    const { open, hover } = this.state;
    const lit = open || hover;
    // The Alchemist's plate holds the shelf as well, so it is a wide one.
    const alchemist = cls.key === 'alchemist';
    const readied = alchemist ? Object.values(this.props.profile?.alchemist?.active || {}).filter((a) => a?.uses > 0).length : 0;
    // The highlight is the class's own colour, not the header gold.
    const tint = (a) => `${cls.accent}${Math.round(a * 255).toString(16).padStart(2, '0')}`;
    return (
      <div className="relative" ref={(el) => { this.root = el; }}>
        <button
          type="button"
          data-tutorial="class-currencies"
          onClick={this.toggle}
          onMouseEnter={() => this.setState({ hover: true })}
          onMouseLeave={() => this.setState({ hover: false })}
          className="relative flex items-center justify-center w-9 h-9 rounded-full overflow-hidden transition-all cursor-pointer"
          style={{
            background: lit ? tint(0.14) : 'rgba(14,12,8,0.55)',
            border: `1.5px solid ${lit ? tint(0.6) : `${GOLD} 0.25)`}`,
            boxShadow: lit ? `0 0 10px ${tint(0.3)}, 0 2px 6px rgba(0,0,0,0.4)` : '0 2px 6px rgba(0,0,0,0.35)',
          }}
          title={t(cls.title)}
        >
          {/* The class's major-node ornament, drawn the way the tree's
              capstones draw it: the path art is white on black, so it is
              read in luminance. */}
          <span
            aria-hidden
            className="vh-skill-capstone-ornament"
            style={{ inset: 0, '--vh-capstone-ornament-svg': `url("${cls.majorOrnament}")`, '--vh-capstone-ornament-color': lit ? cls.accent : TEXT_BODY }}
          />
        </button>
        {readied > 0 ? (
          <div
            className="absolute -top-1 -right-1 min-w-[1cqw] h-[1cqw] rounded-full flex items-center justify-center px-1 pointer-events-none"
            style={{ background: cls.accent, color: '#1a1220', fontSize: '0.5625cqw', fontWeight: 700 }}
            data-testid="class-readied-count"
          >
            {readied}
          </div>
        ) : null}
        <AnimatePresence>
          {open ? (
            <motion.div
              key="plate"
              className="absolute right-0 top-full mt-2 z-20"
              style={{ ...OPAQUE_TEXTURED_PANEL_STYLE, width: alchemist ? '56cqw' : '19cqw', transformOrigin: 'top right' }}
              initial={{ opacity: 0, y: '-0.375cqw', scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: '-0.25cqw', scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              data-testid="class-currencies"
            >
              <FourCorners color={`${GOLD} 0.55)`} size="1.7cqw" />
              <div className={alchemist ? 'px-6 pt-4 pb-6 flex flex-col' : 'px-4 pt-3 pb-4 flex flex-col'} style={{ gap: alchemist ? '1cqw' : '0.75cqw' }}>
                <span className={alchemist ? 'arena-heading' : 'text-xs font-bold arena-heading'} style={{ color: alchemist ? cls.accent : TEXT_PRIMARY, fontSize: alchemist ? '1.5cqw' : undefined }}>{t(cls.title)}</span>
                {alchemist ? <AlchemistPlate profile={this.props.profile} /> : null}
                {alchemist ? null : currencies.map((c) => (
                  <div key={c.key} className="flex flex-col" style={{ gap: '0.375cqw' }}>
                    <div className="flex items-baseline justify-between">
                      <span className="arena-heading" style={{ color: TEXT_PRIMARY, fontSize: '0.8125cqw', letterSpacing: '0.03em' }}>{t(c.label)}</span>
                      <span className="arena-heading tabular-nums" style={{ color: c.value > 0 ? cls.accent : TEXT_MUTED, fontSize: '0.875cqw' }}>
                        {c.max != null ? `${c.value} / ${c.max}` : c.value}
                      </span>
                    </div>
                    {c.max != null ? <VialGauge value={c.value} max={c.max} color={cls.accent} height={0.625} notch={c.max > 12 ? 5 : 1} /> : null}
                  </div>
                ))}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}
